<i18n>
{
	"en": {
		"cancelLabel": "Cancel",
		"chooseAddress": "Select Address",
		"title": "Search for Address",
		"name": "Name",
		"street": "Street",
		"postalPlace": "City",
		"postalCode": "Postal Code",
		"countryCode": "Country Code",
		"phone": "Telephone",
		"email": "Mail",
		"marks": "Marks",
		"failedToFetch": "Failed to search for addresses"
	},
	"no": {
		"cancelLabel": "Avbryt",
		"chooseAddress": "Velg adresse",
		"title": "Søk etter adresse",
		"name": "Navn",
		"street": "Gate",
		"postalPlace": "Poststed",
		"postalCode": "Postnummer",
		"countryCode": "Landkode",
		"phone": "Telefon",
		"email": "Mail",
		"marks": "Marks",
		"failedToFetch": "Failed to search for addresses"
	}
}
</i18n>

<template>
	<t-dialog
		:icon="icon"
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('chooseAddress')"
		:cancelLabel="$t('cancelLabel')"
		:isConfirmDisabled="selectedTripAddress === undefined"
		focusFirstOnShow
		iconType="bp"
		@close="onClose"
		@ok="onOk"
		@shown="onShown"
	>
		<div ref="inputs" class="dialog-inputs">
			<t-search-input
				ref="firstFilter"
				v-model="filter.name"
				:placeholder="$t('name')"
				@input="onInput"
			/>
			<t-search-input
				v-model="filter.street"
				:placeholder="$t('street')"
				@input="onInput"
			/>
			<t-search-input
				v-model="filter.postalPlace"
				:placeholder="$t('postalPlace')"
				@input="onInput"
			/>
		</div>
		<t-grid
			v-if="showGrid"
			ref="grid"
			:items="items"
			:columnDefinitions="columnDefinitions"
			:defaultColumnDefinition="defaultColumnDefinition"
			:rowHeight="40"
			:singleSelect="true"
			:selectableMode="['row']"
			selectable
			style="height: 400px; width: 990px;"
			@selected="setSelectedTripAddress"
			@enter="onEnter"
			@rowDoubleClick="selectAndClose"
		/>
	</t-dialog>
</template>

<script>
import { mapActions } from '../store/tripAddresses.store.js';
import tripAddressesGridMixin from './tripAddressesGridMixin';

export default {
	mixins: [tripAddressesGridMixin],
	props: {
		icon: String,
		isShown: Boolean,
		initialSearchValue: String,
		onlyTerminals: Boolean,
	},
	data() {
		return {
			showGrid: false,
			selectedTripAddress: undefined,
			searchTimeout: undefined,
			filter: {
				name: '',
				postalPlace: '',
				street: '',
			},
			items: []
		};
	},
	created() {
		window.addEventListener('keydown', this.onKeyPress, { capture: true });
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.onKeyPress, { capture: true });
	},
	methods: {
		...mapActions(['fetchFilteredTripAddresses']),
		async onShown() {
			if (this.initialSearchValue) {
				this.$set(this.filter, 'name', this.initialSearchValue);
				this.onInput();
			} else if (this.items.length === 0) {
				this.updateFilteredItems();
			}
			this.showGrid = true;
		},
		async updateFilteredItems() {
			try {
				let filter = { ...this.filter };
				if (this.onlyTerminals) filter.isTerminal = true;
				this.items = await this.fetchFilteredTripAddresses(filter);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},
		onKeyPress(event) {
			if (!this.isShown) return;
			if (this.$children[0].id !== this.$children[0].stack[this.$children[0].stack.length - 1].id) return;
			if (event.key === 'Enter') this.onEnterPressed(event);
			if (event.key === 'F3') this.onF3Pressed(event);
		},
		onEnterPressed(event) {
			if (this.selectedTripAddress) return;
			let { target, shiftKey } = event;
			let tabable = Array.from(this.$refs.inputs.querySelectorAll('input')).filter( x => x.tabIndex >= 0 && !x.disabled);
			let currentIndex = tabable.indexOf(target);
			if (currentIndex === -1) return;

			if (shiftKey) {
				if (currentIndex > 0) {
					tabable[currentIndex - 1].focus();
				} else {
					tabable[tabable.length - 1].focus();
				}
			} else if (currentIndex >= 0 && currentIndex < tabable.length - 1) {
				tabable[currentIndex + 1].focus();
			} else {
				tabable[0].focus();
			}
			event.preventDefault();
			event.stopPropagation();
		},
		onF3Pressed(event) {
			if (!this.$refs.grid) return;
			let inputs = Array.from(this.$refs.inputs.querySelectorAll('input')).filter( x => x.tabIndex >= 0 && !x.disabled );
			if (this.$refs.grid.getFocusedIndex() >= 0) {
				inputs[0].focus();
				this.$refs.grid.removeFocus();
			} else {
				inputs[0].blur();
				this.$refs.grid.focusGrid();
				this.$refs.grid.focusFirst();
			}

			event.preventDefault();
		},
		selectAndClose(addressId) {
			if (!addressId) return;
			this.selectedTripAddress = this.items.find( (x) => x.id === addressId);
			this.onOk();
		},
		onEnter() {
			if (!this.selectedTripAddress) return;
			this.onOk();
		},
		setSelectedTripAddress(addressIds) {
			if (!addressIds) return;
			let address = this.items.find(x => x.id === addressIds[0]);
			this.selectedTripAddress = address;
		},
		onClose() {
			this.$emit('close');
			this.selectedTripAddress = undefined;
			this.filter = {
				name: '',
				postalPlace: '',
				street: ''
			};
			this.showGrid = false;
		},
		onOk() {
			this.$emit('ok', this.selectedTripAddress);
			this.selectedTripAddress = undefined;
			this.filter = {
				name: '',
				postalPlace: '',
				street: ''
			};
			this.showGrid = false;
		},
		onInput() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.updateFilteredItems();
			},200);
		}
	},
};
</script>

<style lang="scss" scoped>
.dialog-inputs {
	display: grid;
	grid-template-columns: repeat(3,1fr);
	grid-column-gap: 5px;
	margin-bottom: 15px;
}
</style>
